import { ChartRecommendation, ColumnInfo } from './dataAnalyzer';
import { ForecastingEngine, ForecastResult } from './forecastingEngine';

export interface ChartData {
  data: any[];
  layout: any;
  forecast?: ForecastResult;
  canForecast?: boolean;
}

export const createChart = (
  recommendation: ChartRecommendation, 
  data: any[], 
  columnInfo: Map<string, ColumnInfo>,
  showForecast: boolean = true
): ChartData => {
  
  const colors = {
    primary: '#6366f1',
    secondary: '#8b5cf6',
    accent: '#06b6d4',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    gradient: ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444']
  };

  const baseLayout = {
    font: { family: 'Inter, system-ui, sans-serif', size: 12 },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    margin: { l: 60, r: 40, t: 60, b: 60 },
    hovermode: 'closest',
    showlegend: true,
    legend: {
      bgcolor: 'rgba(255,255,255,0.9)',
      bordercolor: '#e5e7eb',
      borderwidth: 1
    }
  };

  switch (recommendation.type) {
    case 'scatter':
      return createScatterPlot(recommendation, data, colors, baseLayout);
    
    case 'bar':
      return createBarChart(recommendation, data, colors, baseLayout);
    
    case 'line':
      return createLineChart(recommendation, data, colors, baseLayout, showForecast);
    
    case 'pie':
      return createPieChart(recommendation, data, colors, baseLayout);
    
    case 'histogram':
      return createHistogram(recommendation, data, colors, baseLayout);
    
    case 'box':
      return createBoxPlot(recommendation, data, colors, baseLayout);
    
    case 'heatmap':
      return createHeatmap(recommendation, data, columnInfo, colors, baseLayout);
    
    case 'scatter3d':
      return create3DScatter(recommendation, data, colors, baseLayout);
    
    case 'sunburst':
      return createSunburst(recommendation, data, colors, baseLayout);
    
    case 'violin':
      return createViolinPlot(recommendation, data, colors, baseLayout);
    
    default:
      return createBarChart(recommendation, data, colors, baseLayout);
  }
};

const createScatterPlot = (rec: ChartRecommendation, data: any[], colors: any, baseLayout: any): ChartData => {
  const xCol = rec.columns[0];
  const yCol = rec.columns[1];
  
  return {
    data: [{
      x: data.map(row => Number(row[xCol])).filter(v => !isNaN(v)),
      y: data.map(row => Number(row[yCol])).filter(v => !isNaN(v)),
      mode: 'markers',
      type: 'scatter',
      marker: {
        color: colors.primary,
        size: 8,
        opacity: 0.7,
        line: { color: 'white', width: 1 }
      },
      name: `${xCol} vs ${yCol}`
    }],
    layout: {
      ...baseLayout,
      title: rec.title,
      xaxis: { title: xCol, gridcolor: '#f3f4f6' },
      yaxis: { title: yCol, gridcolor: '#f3f4f6' }
    }
  };
};

const createBarChart = (rec: ChartRecommendation, data: any[], colors: any, baseLayout: any): ChartData => {
  const categoryCol = rec.columns[0];
  const valueCol = rec.columns[1];
  
  const grouped = data.reduce((acc, row) => {
    const category = row[categoryCol];
    const value = Number(row[valueCol]);
    if (!isNaN(value)) {
      acc[category] = acc[category] || { sum: 0, count: 0 };
      acc[category].sum += value;
      acc[category].count += 1;
    }
    return acc;
  }, {} as Record<string, { sum: number; count: number }>);

  const averages = (Object.entries(grouped) as Array<[string, { sum: number; count: number }]>).map(([category, stats]) => [
    category,
    stats.count > 0 ? stats.sum / stats.count : 0
  ]);

  const sortedEntries = averages
    .sort(([,a], [,b]) => (b as number) - (a as number))
    .slice(0, 15);

  return {
    data: [{
      x: sortedEntries.map(([category]) => category),
      y: sortedEntries.map(([, value]) => value),
      type: 'bar',
      marker: {
        color: colors.gradient,
        line: { color: 'white', width: 1 }
      },
      name: `Avg ${valueCol}`
    }],
    layout: {
      ...baseLayout,
      title: rec.title,
      xaxis: { title: categoryCol, tickangle: -45 },
      yaxis: { title: `Average ${valueCol}`, gridcolor: '#f3f4f6' }
    }
  };
};

const createLineChart = (rec: ChartRecommendation, data: any[], colors: any, baseLayout: any, showForecast: boolean = true): ChartData => {
  const xCol = rec.columns[0];
  const yCol = rec.columns[1];
  
  const sortedData = data
    .filter(row => row[xCol] && !isNaN(Number(row[yCol])))
    .sort((a, b) => new Date(a[xCol]).getTime() - new Date(b[xCol]).getTime());

  // Check if this data is suitable for forecasting
  const canForecast = ForecastingEngine.isTimeSeriesData(sortedData, xCol, yCol);
  let forecast: ForecastResult | undefined;
  
  if (canForecast && sortedData.length >= 6 && showForecast) {
    try {
      const timeSeriesData = ForecastingEngine.convertToTimeSeriesData(sortedData, xCol, yCol);
      const engine = new ForecastingEngine(timeSeriesData, { periods: 6 });
      const forecastResult = engine.forecast();
      forecast = forecastResult || undefined;
    } catch (error) {
      console.warn('Forecasting failed:', error);
    }
  }

  // Prepare chart data
  const chartData: any[] = [{
    x: sortedData.map(row => row[xCol]),
    y: sortedData.map(row => Number(row[yCol])),
    type: 'scatter',
    mode: 'lines+markers',
    line: { color: colors.primary, width: 3 },
    marker: { color: colors.primary, size: 6 },
    name: 'Historical Data'
  }];

  // Add forecast data if available
  if (forecast) {
    const forecastPoints = forecast.data.filter(d => !d.original);
    if (forecastPoints.length > 0) {
      chartData.push({
        x: forecastPoints.map(d => d.date.toISOString().split('T')[0]),
        y: forecastPoints.map(d => d.value),
        type: 'scatter',
        mode: 'lines+markers',
        line: { 
          color: colors.secondary || '#ef4444', 
          width: 2, 
          dash: 'dash' 
        },
        marker: { 
          color: colors.secondary || '#ef4444', 
          size: 5,
          symbol: 'diamond'
        },
        name: `6-Month Forecast (${forecast.confidence} confidence)`,
        hovertemplate: '<b>Forecasted</b><br>' +
                      'Date: %{x}<br>' +
                      'Value: %{y:.2f}<br>' +
                      `Method: ${forecast.method}<br>` +
                      `Confidence: ${forecast.confidence}<br>` +
                      '<extra></extra>'
      });

      // Add a connecting line between last historical point and first forecast point
      const lastHistorical = sortedData[sortedData.length - 1];
      const firstForecast = forecastPoints[0];
      
      chartData.push({
        x: [lastHistorical[xCol], firstForecast.date.toISOString().split('T')[0]],
        y: [Number(lastHistorical[yCol]), firstForecast.value],
        type: 'scatter',
        mode: 'lines',
        line: { 
          color: colors.secondary || '#ef4444', 
          width: 1, 
          dash: 'dot' 
        },
        showlegend: false,
        hoverinfo: 'skip'
      });
    }
  }

  return {
    data: chartData,
    layout: {
      ...baseLayout,
      title: rec.title + (forecast ? ` (with ${forecast.method.replace('_', ' ')} forecast)` : ''),
      xaxis: { title: xCol, gridcolor: '#f3f4f6' },
      yaxis: { title: yCol, gridcolor: '#f3f4f6' },
      annotations: forecast ? [{
        x: 1,
        y: 1,
        xref: 'paper',
        yref: 'paper',
        text: `Trend: ${forecast.trend} | Accuracy: ${(forecast.accuracy! * 100).toFixed(1)}%`,
        showarrow: false,
        font: { size: 10, color: '#6b7280' },
        bgcolor: 'rgba(255,255,255,0.8)',
        bordercolor: '#e5e7eb',
        borderwidth: 1
      }] : []
    },
    forecast,
    canForecast
  };
};

const createPieChart = (rec: ChartRecommendation, data: any[], colors: any, baseLayout: any): ChartData => {
  const column = rec.columns[0];
  const counts = data.reduce((acc, row) => {
    const value = row[column];
    acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {});

  const sortedEntries = Object.entries(counts)
    .sort(([,a], [,b]) => (b as number) - (a as number))
    .slice(0, 10);

  return {
    data: [{
      labels: sortedEntries.map(([label]) => label),
      values: sortedEntries.map(([, value]) => value),
      type: 'pie',
      marker: { colors: colors.gradient },
      textinfo: 'label+percent',
      textposition: 'outside',
      hovertemplate: '<b>%{label}</b><br>Count: %{value}<br>Percentage: %{percent}<extra></extra>'
    }],
    layout: {
      ...baseLayout,
      title: rec.title,
      showlegend: false
    }
  };
};

const createHistogram = (rec: ChartRecommendation, data: any[], colors: any, baseLayout: any): ChartData => {
  const column = rec.columns[0];
  const values = data.map(row => Number(row[column])).filter(v => !isNaN(v));

  return {
    data: [{
      x: values,
      type: 'histogram',
      marker: {
        color: colors.primary,
        opacity: 0.7,
        line: { color: 'white', width: 1 }
      },
      name: column
    }],
    layout: {
      ...baseLayout,
      title: rec.title,
      xaxis: { title: column, gridcolor: '#f3f4f6' },
      yaxis: { title: 'Count', gridcolor: '#f3f4f6' }
    }
  };
};

const createBoxPlot = (rec: ChartRecommendation, data: any[], colors: any, baseLayout: any): ChartData => {
  const categoryCol = rec.columns[0];
  const valueCol = rec.columns[1];
  
  const categories = Array.from(new Set(data.map(row => row[categoryCol]))).slice(0, 10);
  
  return {
    data: categories.map((category, index) => ({
      y: data
        .filter(row => row[categoryCol] === category)
        .map(row => Number(row[valueCol]))
        .filter(v => !isNaN(v)),
      type: 'box',
      name: category,
      marker: { color: colors.gradient[index % colors.gradient.length] }
    })),
    layout: {
      ...baseLayout,
      title: rec.title,
      yaxis: { title: valueCol, gridcolor: '#f3f4f6' }
    }
  };
};

const createHeatmap = (rec: ChartRecommendation, data: any[], columnInfo: Map<string, ColumnInfo>, colors: any, baseLayout: any): ChartData => {
  const numericColumns = rec.columns.filter(col => {
    const info = columnInfo.get(col);
    return info && info.type === 'numeric';
  });

  const correlationMatrix: number[][] = [];
  const labels: string[] = [];

  numericColumns.forEach((col1, i) => {
    correlationMatrix[i] = [];
    labels[i] = col1;
    
    numericColumns.forEach((col2, j) => {
      const values1 = data.map(row => Number(row[col1])).filter(v => !isNaN(v));
      const values2 = data.map(row => Number(row[col2])).filter(v => !isNaN(v));
      
      const correlation = calculateCorrelation(values1, values2);
      correlationMatrix[i][j] = correlation;
    });
  });

  return {
    data: [{
      z: correlationMatrix,
      x: labels,
      y: labels,
      type: 'heatmap',
      colorscale: [
        [0, '#3b82f6'],
        [0.5, '#ffffff'],
        [1, '#ef4444']
      ],
      zmid: 0,
      text: correlationMatrix.map(row => 
        row.map(val => val.toFixed(2))
      ),
      texttemplate: '%{text}',
      textfont: { size: 12 }
    }],
    layout: {
      ...baseLayout,
      title: rec.title,
      xaxis: { title: '', tickangle: -45 },
      yaxis: { title: '' }
    }
  };
};

const create3DScatter = (rec: ChartRecommendation, data: any[], colors: any, baseLayout: any): ChartData => {
  const [xCol, yCol, zCol] = rec.columns;
  
  return {
    data: [{
      x: data.map(row => Number(row[xCol])).filter(v => !isNaN(v)),
      y: data.map(row => Number(row[yCol])).filter(v => !isNaN(v)),
      z: data.map(row => Number(row[zCol])).filter(v => !isNaN(v)),
      mode: 'markers',
      type: 'scatter3d',
      marker: {
        color: colors.primary,
        size: 5,
        opacity: 0.8
      }
    }],
    layout: {
      ...baseLayout,
      title: rec.title,
      scene: {
        xaxis: { title: xCol },
        yaxis: { title: yCol },
        zaxis: { title: zCol }
      }
    }
  };
};

const createSunburst = (rec: ChartRecommendation, data: any[], colors: any, baseLayout: any): ChartData => {
  const [parent, child] = rec.columns;
  
  const hierarchy = data.reduce((acc, row) => {
    const parentVal = row[parent];
    const childVal = row[child];
    const key = `${parentVal} - ${childVal}`;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const ids: string[] = [];
  const labels: string[] = [];
  const parents: string[] = [];
  const values: number[] = [];

  Object.entries(hierarchy).forEach(([key, count]) => {
    const [parentVal, childVal] = key.split(' - ');
    ids.push(key);
    labels.push(childVal);
    parents.push(parentVal);
    values.push(count as number);
  });

  return {
    data: [{
      ids,
      labels,
      parents,
      values,
      type: 'sunburst',
      branchvalues: 'total'
    }],
    layout: {
      ...baseLayout,
      title: rec.title
    }
  };
};

const createViolinPlot = (rec: ChartRecommendation, data: any[], colors: any, baseLayout: any): ChartData => {
  const categoryCol = rec.columns[0];
  const valueCol = rec.columns[1];
  
  const categories = Array.from(new Set(data.map(row => row[categoryCol]))).slice(0, 8);
  
  return {
    data: categories.map((category, index) => ({
      y: data
        .filter(row => row[categoryCol] === category)
        .map(row => Number(row[valueCol]))
        .filter(v => !isNaN(v)),
      type: 'violin',
      name: category,
      fillcolor: colors.gradient[index % colors.gradient.length],
      line: { color: colors.gradient[index % colors.gradient.length] }
    })),
    layout: {
      ...baseLayout,
      title: rec.title,
      yaxis: { title: valueCol, gridcolor: '#f3f4f6' }
    }
  };
};

const calculateCorrelation = (x: number[], y: number[]): number => {
  const n = Math.min(x.length, y.length);
  if (n === 0) return 0;
  
  const meanX = x.reduce((a, b) => a + b, 0) / n;
  const meanY = y.reduce((a, b) => a + b, 0) / n;
  
  let numerator = 0;
  let sumXSquared = 0;
  let sumYSquared = 0;
  
  for (let i = 0; i < n; i++) {
    const deltaX = x[i] - meanX;
    const deltaY = y[i] - meanY;
    numerator += deltaX * deltaY;
    sumXSquared += deltaX * deltaX;
    sumYSquared += deltaY * deltaY;
  }
  
  const denominator = Math.sqrt(sumXSquared * sumYSquared);
  return denominator === 0 ? 0 : numerator / denominator;
};
